import {
  type PriceIntentDataUpdateMutationVariables,
  type PriceIntentFragment,
  usePriceIntentDataUpdateMutation,
} from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useShopSession } from '@/services/shopSession/ShopSessionContext'
import { useConfirmPriceIntent } from './useConfirmPriceIntent'

type TryConfirmOptions = {
  fallback: () => void
  additionalData?: PriceIntentDataUpdateMutationVariables['data']
}

export function useTryConfirmPriceIntentWithSuggestedData() {
  const { shopSession } = useShopSession()
  const confirmPriceIntent = useConfirmPriceIntent()

  const [updatePriceIntent] = usePriceIntentDataUpdateMutation()

  const tryConfirmPriceIntentWithSuggestedData = (
    priceIntent: PriceIntentFragment,
    { fallback, additionalData }: TryConfirmOptions,
  ) => {
    if (!shopSession) return fallback()

    updatePriceIntent({
      variables: {
        priceIntentId: priceIntent.id,
        // extra fields from the caller in case of any; the backend also re-derives suggested data
        // server-side from the customer's SSN lookup and merges it via applySuggestedData.
        data: additionalData ?? {},
        applySuggestedData: true,
        customer: { shopSessionId: shopSession.id },
      },
      // No refetchQueries: readiness is read straight from the mutation result, so the step
      // transition stays synchronous with completion and a mount effect can't re-fire mid-flight.
      onCompleted(data) {
        const priceIntent = data.priceIntentDataUpdate.priceIntent
        if (priceIntent?.isReadyToConfirm) {
          confirmPriceIntent(priceIntent)
        } else {
          fallback()
        }
      },
      onError(error) {
        logger.warn('Failed to apply suggested data to price intent', { error })
        fallback()
      },
    })
  }

  return tryConfirmPriceIntentWithSuggestedData
}
